import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScanSearch, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import ProposalDetailDialog from './ProposalDetailDialog';
import { API, STATUS_META, errorText, sourceLabel } from './constants';
import { SimilarDecisions } from '../memory/SimilarDecisions';

const NewProposalForm = ({ companyId, onDone }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({ title: '', amount: '', risk_level: '', category: '' });
  const create = useMutation({
    mutationFn: () => axios.post(`${API}/companies/${companyId}/proposals`, {
      title: f.title, category: f.category || null, amount: f.amount === '' ? null : Number(f.amount), risk_level: f.risk_level || null }),
    onSuccess: (r) => {
      const pr = r.data.proposal;
      const msg = { pending: 'Propuesta enviada a aprobación', blocked: 'Propuesta bloqueada por una regla institucional', auto_cleared: 'No requiere aprobación' }[pr.status];
      toast[pr.status === 'blocked' ? 'error' : 'success'](msg);
      (pr.warnings || []).forEach((w) => toast.warning(`Advertencia: ${w.name}`));
      qc.invalidateQueries({ queryKey: ['v2-proposals', companyId] }); onDone();
    },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <Card><CardContent className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 p-4" data-testid="new-proposal-form">
      <Input placeholder="Decisión propuesta" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="new-proposal-title" />
      <Input placeholder="Monto" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="new-proposal-amount" />
      <Input placeholder="Riesgo (low…critical)" value={f.risk_level} onChange={(e) => setF({ ...f, risk_level: e.target.value })} data-testid="new-proposal-risk" />
      <Input placeholder="Categoría" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="new-proposal-category" />
      <Button onClick={() => create.mutate()} disabled={f.title.length < 3 || create.isPending} data-testid="new-proposal-submit">Enviar</Button>
      <div className="col-span-full"><SimilarDecisions companyId={companyId} text={`${f.title} ${f.category}`} testid="new-proposal-similar" /></div>
    </CardContent></Card>
  );
};

export const ApprovalsInbox = ({ companyId, user }) => {
  const qc = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['v2-proposals', companyId, status],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/proposals${status ? `?status=${status}` : ''}`)).data,
    enabled: !!companyId, refetchInterval: 30000,
  });
  const scan = useMutation({
    mutationFn: () => axios.post(`${API}/companies/${companyId}/proposals/scan`),
    onSuccess: (r) => { toast.success(r.data.skipped ? 'Crea una regla activa para analizar insights' : `${r.data.scanned} insights analizados · ${r.data.proposals} decisiones`); qc.invalidateQueries({ queryKey: ['v2-proposals', companyId] }); },
    onError: (e) => toast.error(errorText(e)),
  });
  const counts = data?.counts || {};
  return (
    <div className="space-y-4" data-testid="approvals-inbox">
      <div className="flex flex-wrap items-center gap-2">
        {[['pending'], ['blocked'], ['approved'], ['rejected'], ['expired'], ['auto_cleared'], ['']].map(([s]) => (
          <Button key={s || 'all'} size="sm" variant={status === s ? 'default' : 'outline'} onClick={() => setStatus(s)} data-testid={`approvals-filter-${s || 'all'}`}>
            {s ? STATUS_META[s].label : 'Todas'} {s && <span className="ml-1 opacity-70">{counts[s] || 0}</span>}
          </Button>
        ))}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowNew((v) => !v)} data-testid="new-proposal-toggle"><Plus className="mr-1 h-4 w-4" />Propuesta manual</Button>
          <Button size="sm" onClick={() => scan.mutate()} disabled={scan.isPending} data-testid="approvals-scan-btn"><ScanSearch className="mr-1 h-4 w-4" />{scan.isPending ? 'Analizando…' : 'Analizar insights de agentes'}</Button>
        </div>
      </div>
      {showNew && <NewProposalForm companyId={companyId} onDone={() => setShowNew(false)} />}
      {isLoading && <p className="text-sm text-slate-400">Cargando…</p>}
      {!isLoading && !(data?.proposals || []).length && <p className="text-sm text-slate-400" data-testid="approvals-empty">No hay decisiones en este estado.</p>}
      <div className="space-y-2">
        {(data?.proposals || []).map((p) => (
          <button key={p.id} onClick={() => setOpenId(p.id)} data-testid={`proposal-row-${p.id}`}
            className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left transition-colors duration-150 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800">
            <div className="min-w-0">
              <p className="truncate font-medium text-slate-900 dark:text-white">{p.title}</p>
              <p className="text-xs text-slate-500">{sourceLabel(p)}{p.source?.type === 'manual' ? ` · ${p.created_by}` : ''} · {new Date(p.created_at).toLocaleString()}{p.amount != null ? ` · Q${Number(p.amount).toLocaleString()}` : ''}{p.routing ? ` · ${p.routing.iad_code} → ${p.routing.gov}` : ''}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {p.warnings?.length > 0 && <Badge className="bg-orange-100 text-orange-800" data-testid={`proposal-row-warn-${p.id}`}>{p.warnings.length} advertencia{p.warnings.length > 1 ? 's' : ''}</Badge>}
              {p.status === 'pending' && p.approvals?.length > 0 && <Badge variant="outline">{p.approvals.length} aprobación{p.approvals.length > 1 ? 'es' : ''}</Badge>}
              <Badge className={STATUS_META[p.status]?.cls}>{STATUS_META[p.status]?.label}</Badge>
            </div>
          </button>
        ))}
      </div>
      {openId && <ProposalDetailDialog companyId={companyId} proposalId={openId} user={user} onClose={() => setOpenId(null)} />}
    </div>
  );
};
